import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  BusyIndicator,
  Button,
  CheckBox,
  Icon,
  IllustratedMessage,
  Input,
  Label,
  MessageStrip,
  Text,
  Title,
} from "@ui5/webcomponents-react";
import { toast } from "sonner";
import { PublicBookingApi } from "../../api/client";

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const pad2 = (n) => String(n).padStart(2, "0");
const isoFromDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const isoFromGerman = (s) => { const [d, m, y] = s.split("."); return `${y}-${m}-${d}`; };
const monthIndex = ({ year, month }) => year * 12 + month;

const monthLabel = (year, month) =>
  new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" }).format(new Date(year, month, 1));
const longDate = (iso) =>
  new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "2-digit", month: "long" }).format(new Date(`${iso}T00:00:00`));

function monthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday = 0
  return Array.from({ length: 42 }, (_, i) => new Date(year, month, 1 - startOffset + i));
}

// Start cells from which `duration` minutes can be covered by consecutive (gap-free) slots.
// duration falsy → no length restriction, every slot is its own appointment.
function bookableStarts(daySlots, duration) {
  if (!duration) return daySlots;
  const out = [];
  for (let i = 0; i < daySlots.length; i++) {
    let acc = 0;
    let prevEnd = null;
    for (let j = i; j < daySlots.length; j++) {
      const s = daySlots[j];
      if (prevEnd !== null && s.startsAt !== prevEnd) break; // non-contiguous
      acc += s.durationMinutes;
      prevEnd = s.endsAt;
      if (acc === duration) { out.push(daySlots[i]); break; }
      if (acc > duration) break;
    }
  }
  return out;
}

// "30 Min", "1 Std", "1 Std 30 Min"
function durationLabel(min) {
  if (min < 60) return `${min} Min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h} Std` : `${h} Std ${m} Min`;
}

// End-of-appointment clock time for the chosen start + duration (falls back to the slot's own length).
function endTimeLabel(slot, duration) {
  const start = new Date(slot.startsAt);
  const end = new Date(start.getTime() + (duration || slot.durationMinutes) * 60000);
  return new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(end);
}

// Per-link, per-browser memory of the customer's own bookings (the "cookie" recognition):
// stores each booking's manage token so they can cancel/reschedule without re-entering their name.
const managedKey = (token) => `calendery_booked_${token}`;
function loadManaged(token) {
  try {
    const v = JSON.parse(localStorage.getItem(managedKey(token)) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
function saveManaged(token, list) {
  try {
    localStorage.setItem(managedKey(token), JSON.stringify(list));
  } catch {
    /* storage unavailable (private mode) — manage still works via name lookup */
  }
}

// Build + download a minimal .ics for the confirmed booking (client-side "add to calendar").
function downloadIcs(result) {
  const utc = (iso) => new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const esc = (s) => String(s || "").replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
  let desc = "Tennis-Termin.";
  if (result.meetUrl) desc += "\n\nGoogle Meet: " + result.meetUrl;
  if (result.note) desc += "\n\n" + result.note;
  const lines = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//tennis-software.de//Calendery//DE", "BEGIN:VEVENT",
    // Stable UID (start+end of this booking) so re-downloading updates the event, not duplicates it.
    "UID:" + utc(result.startsAt) + "-" + utc(result.endsAt) + "@tennis-software.de",
    "DTSTAMP:" + utc(new Date().toISOString()),
    "DTSTART:" + utc(result.startsAt), "DTEND:" + utc(result.endsAt),
    "SUMMARY:" + esc(result.meetUrl ? "Tennis-Termin (Online)" : "Tennis-Termin"),
    "DESCRIPTION:" + esc(desc),
  ];
  if (result.meetUrl) { lines.push("LOCATION:" + esc(result.meetUrl)); lines.push("URL:" + result.meetUrl); }
  lines.push("END:VEVENT", "END:VCALENDAR");
  const blob = new Blob([lines.join("\r\n") + "\r\n"], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "termin.ics";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Shell({ children }) {
  return (
    <div className="public-shell">
      <div className="brand-header">
        <span className="logo" role="img" aria-label="Tennis">🎾</span>
        <h1>Tennis-Software · Terminbuchung</h1>
      </div>
      <div className="public-card">{children}</div>
    </div>
  );
}

export default function BookingPage() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading | ready | invalid | confirmed
  const [linkLabel, setLinkLabel] = useState("");
  const [privacyNotice, setPrivacyNotice] = useState("");
  const [slotsByDate, setSlotsByDate] = useState(new Map());
  const [bounds, setBounds] = useState(null); // { min:{year,month}, max:{year,month} }
  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [allowedDurations, setAllowedDurations] = useState(null); // null = no length restriction
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [form, setForm] = useState({ customerName: "", customerEmail: "", privacyAccepted: false });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [managed, setManaged] = useState([]); // this browser's known bookings on this link
  const [rescheduleToken, setRescheduleToken] = useState(null); // manage token being rescheduled (or null)
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupName, setLookupName] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const pollRef = useRef(null);
  const timesRef = useRef(null);
  const formRef = useRef(null);
  const calRef = useRef(null);

  useEffect(() => { setManaged(loadManaged(token)); }, [token]);

  const rememberBooking = useCallback((entry, replaceToken = null) => {
    setManaged((prev) => {
      const next = [...prev.filter((e) => e.manageToken !== entry.manageToken && e.manageToken !== replaceToken), entry];
      saveManaged(token, next);
      return next;
    });
  }, [token]);

  const forgetBooking = useCallback((manageToken) => {
    setManaged((prev) => {
      const next = prev.filter((e) => e.manageToken !== manageToken);
      saveManaged(token, next);
      return next;
    });
  }, [token]);

  const loadSlots = useCallback(async ({ preserveView = false } = {}) => {
    try {
      const data = await PublicBookingApi.getBooking(token);
      setLinkLabel(data.link.label);
      setPrivacyNotice(data.privacyNotice);

      const durations = data.link.allowedDurations && data.link.allowedDurations.length ? data.link.allowedDurations : null;
      setAllowedDurations(durations);
      setSelectedDuration((cur) => (durations ? (durations.includes(cur) ? cur : durations[0]) : null));

      const map = new Map();
      for (const slot of data.slots) {
        const iso = isoFromGerman(slot.date);
        if (!map.has(iso)) map.set(iso, []);
        map.get(iso).push(slot);
      }
      setSlotsByDate(map);

      const isos = [...map.keys()].sort();
      if (isos.length > 0) {
        const toYM = (iso) => { const [y, m] = iso.split("-"); return { year: +y, month: +m - 1 }; };
        const min = toYM(isos[0]);
        const max = toYM(isos[isos.length - 1]);
        setBounds({ min, max });
        if (!preserveView) {
          setCursor(min);
        } else {
          // Keep the user's month, but clamp it into the (possibly shrunk) availability bounds
          // so a reload can't strand them on an empty out-of-range month.
          setCursor((c) => {
            const ci = monthIndex(c);
            if (ci < monthIndex(min)) return min;
            if (ci > monthIndex(max)) return max;
            return c;
          });
        }
      }
      setPhase("ready");
    } catch (e) {
      // Only a 404 means the link is really invalid/expired; anything else is a transient error.
      setPhase(e.response?.status === 404 ? "invalid" : "error");
    }
  }, [token]);

  useEffect(() => {
    loadSlots();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [loadSlots]);

  const cells = useMemo(() => monthMatrix(cursor.year, cursor.month), [cursor]);
  const daySlots = useMemo(() => (selectedDate ? slotsByDate.get(selectedDate) || [] : []), [selectedDate, slotsByDate]);
  const dayStarts = useMemo(() => bookableStarts(daySlots, selectedDuration), [daySlots, selectedDuration]);

  // Per-day count of bookable start times for the chosen duration; a day only counts as
  // "free" if it can actually host the selected length (e.g. a lone 30-min cell can't host 60).
  const bookableByDate = useMemo(() => {
    const m = new Map();
    for (const [iso, slots] of slotsByDate) m.set(iso, bookableStarts(slots, selectedDuration).length);
    return m;
  }, [slotsByDate, selectedDuration]);

  const chooseDuration = (d) => {
    setSelectedDuration(d);
    setSelectedSlot(null); // available start times change with the length
  };

  const canPrev = bounds && monthIndex(cursor) > monthIndex(bounds.min);
  const canNext = bounds && monthIndex(cursor) < monthIndex(bounds.max);
  const step = (delta) => {
    const m = cursor.month + delta;
    setCursor({ year: cursor.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 });
    // A selection from the previous month must not stay submittable off-screen.
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const selectDay = (iso) => {
    // A spill-over cell (e.g. the 1st/2nd of the next month shown in this month's grid)
    // belongs to another month — jump the view there so the header + selection stay in sync.
    const [y, m] = iso.split("-");
    const ym = { year: +y, month: +m - 1 };
    if (ym.year !== cursor.year || ym.month !== cursor.month) setCursor(ym);
    setSelectedDate(iso);
    setSelectedSlot(null);
    setFieldErrors({});
    setTimeout(() => timesRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
  };
  const selectTime = (slot) => {
    setSelectedSlot(slot);
    setFieldErrors({});
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
  };

  const pollForMeet = useCallback((resultToken) => {
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const r = await PublicBookingApi.getResult(resultToken);
        if (r.meetUrl || !r.meetPending || tries > 12) {
          clearInterval(pollRef.current);
          const gaveUp = !r.meetUrl && tries > 12;
          setResult((prev) => ({ ...prev, meetUrl: r.meetUrl, meetPending: r.meetPending, meetGaveUp: gaveUp }));
        }
      } catch {
        if (tries > 12) clearInterval(pollRef.current);
      }
    }, 1500);
  }, []);

  const submit = async () => {
    setFieldErrors({});
    setSubmitting(true);
    try {
      const data = await PublicBookingApi.createBooking(token, {
        slotId: selectedSlot.id,
        durationMinutes: selectedDuration || 0,
        ...form,
      });
      // Remember this booking in the browser so the customer can manage it later (no re-login).
      if (data.manageToken) {
        rememberBooking({ manageToken: data.manageToken, appointment: data.appointment, customerName: form.customerName });
      }
      setResult(data);
      setPhase("confirmed");
      if (data.meetPending) pollForMeet(data.resultToken);
    } catch (e) {
      const res = e.response;
      if (res?.status === 422) {
        setFieldErrors(res.data.fields || {});
      } else if ([409, 403, 404].includes(res?.status)) {
        toast.error(
          409 === res.status
            ? "Dieser Termin wurde leider gerade vergeben. Bitte wähle einen anderen."
            : "Dieser Termin ist nicht mehr verfügbar. Bitte wähle einen anderen.",
        );
        setSelectedSlot(null);
        setSelectedDate(null);
        loadSlots({ preserveView: true });
      } else {
        toast.error("Buchung fehlgeschlagen. Bitte versuche es erneut.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const startReschedule = (entry) => {
    setRescheduleToken(entry.manageToken);
    setSelectedDate(null);
    setSelectedSlot(null);
    setFieldErrors({});
    setTimeout(() => calRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const cancelBooking = async (entry) => {
    if (typeof window !== "undefined" && !window.confirm("Diesen Termin wirklich stornieren?")) return;
    try {
      await PublicBookingApi.cancel(entry.manageToken);
      forgetBooking(entry.manageToken);
      if (rescheduleToken === entry.manageToken) setRescheduleToken(null);
      toast.success("Termin storniert.");
      loadSlots({ preserveView: true });
    } catch {
      toast.error("Stornierung fehlgeschlagen. Bitte versuche es erneut.");
    }
  };

  const doReschedule = async () => {
    setSubmitting(true);
    try {
      const prev = managed.find((e) => e.manageToken === rescheduleToken);
      const data = await PublicBookingApi.reschedule(rescheduleToken, {
        slotId: selectedSlot.id,
        durationMinutes: selectedDuration || 0,
      });
      // Reschedule re-signs the manage token (new expiry) → replace the old entry.
      rememberBooking({ manageToken: data.manageToken, appointment: data.appointment, customerName: prev?.customerName }, rescheduleToken);
      setRescheduleToken(null);
      setResult({ ...data, rescheduled: true });
      setPhase("confirmed");
      if (data.meetPending) pollForMeet(data.resultToken);
    } catch (e) {
      const res = e.response;
      if ([409, 403, 404].includes(res?.status)) {
        toast.error("Dieser Termin ist nicht mehr verfügbar. Bitte wähle einen anderen.");
        setSelectedSlot(null);
        setSelectedDate(null);
        loadSlots({ preserveView: true });
      } else {
        toast.error("Umbuchung fehlgeschlagen. Bitte versuche es erneut.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const findByName = async () => {
    if (lookupName.trim().length < 2) { toast.error("Bitte gib deinen Nachnamen ein."); return; }
    setLookupBusy(true);
    try {
      const data = await PublicBookingApi.findBookings(token, lookupName.trim());
      if (!data.bookings.length) {
        toast.message("Keine aktive Buchung mit diesem Namen gefunden.");
      } else {
        data.bookings.forEach((b) => rememberBooking({ manageToken: b.manageToken, appointment: b.appointment, customerName: b.customerName }));
        setLookupOpen(false);
        setLookupName("");
        toast.success(data.bookings.length === 1 ? "Buchung gefunden." : `${data.bookings.length} Buchungen gefunden.`);
      }
    } catch (e) {
      toast.error(e.response?.status === 429 ? "Zu viele Versuche. Bitte kurz warten." : "Suche fehlgeschlagen.");
    } finally {
      setLookupBusy(false);
    }
  };

  if (phase === "loading") {
    return <Shell><div className="center-screen"><BusyIndicator active size="L" /><Text>Verfügbare Termine werden geladen…</Text></div></Shell>;
  }

  if (phase === "invalid") {
    return (
      <Shell>
        <IllustratedMessage name="UnableToLoad" titleText="Buchungslink ungültig"
          subtitleText="Dieser Link ist abgelaufen oder ungültig. Bitte fordere einen neuen Buchungslink an." />
      </Shell>
    );
  }

  if (phase === "error") {
    return (
      <Shell>
        <IllustratedMessage name="UnableToLoad" titleText="Verbindungsproblem"
          subtitleText="Die Termine konnten gerade nicht geladen werden. Bitte versuche es in Kürze erneut." />
      </Shell>
    );
  }

  if (phase === "confirmed") {
    return (
      <Shell>
        <div className="center-screen">
          <Icon name="sys-enter-2" style={{ color: "#107e3e", fontSize: "3rem" }} />
          <Title level="H2">{result.rescheduled ? "Termin umgebucht!" : "Termin bestätigt!"}</Title>
          <Text>{result.appointment}</Text>
          {result.note && (
            <MessageStrip design="Information" hideCloseButton style={{ marginTop: 8, whiteSpace: "pre-line" }}>
              {result.note}
            </MessageStrip>
          )}
          <div style={{ marginTop: 8 }}>
            {result.meetUrl ? (
              <Button design="Emphasized" icon="video" onClick={() => window.open(result.meetUrl, "_blank")}>Google Meet öffnen</Button>
            ) : result.meetPending ? (
              <MessageStrip design="Information" hideCloseButton>
                {result.meetGaveUp
                  ? "Dein Google-Meet-Link folgt in Kürze per E-Mail."
                  : "Dein Google-Meet-Link wird erstellt und kommt zusammen mit der Bestätigung per E-Mail."}
              </MessageStrip>
            ) : null}
          </div>
          <div style={{ marginTop: 10 }}>
            <Button design="Transparent" icon="add-calendar" onClick={() => downloadIcs(result)}>Zum Kalender hinzufügen</Button>
          </div>
          <Text className="muted" style={{ marginTop: 12 }}>
            Eine Bestätigung mit Kalendereintrag (.ics) und Storno-Link wurde an deine E-Mail-Adresse gesendet.
          </Text>
        </div>
      </Shell>
    );
  }

  const hasAnySlots = slotsByDate.size > 0;
  const hasBookable = [...bookableByDate.values()].some((c) => c > 0);

  return (
    <Shell>
      <Title level="H2" style={{ marginBottom: 4 }}>{linkLabel}</Title>
      <Text className="muted">
        {allowedDurations ? "Wähle eine Dauer und einen Tag mit freien Terminen." : "Wähle einen Tag mit freien Terminen."}
      </Text>

      {(managed.length > 0 || lookupOpen) && (
        <div className="manage-panel">
          {managed.length > 0 && <h3>Deine Termine</h3>}
          {managed.map((m) => (
            <div key={m.manageToken} className="manage-row">
              <span>{m.appointment}{m.customerName ? ` · ${m.customerName}` : ""}</span>
              <span className="manage-actions">
                <Button design="Transparent" icon="edit" onClick={() => startReschedule(m)}>Umbuchen</Button>
                <Button design="Transparent" icon="decline" onClick={() => cancelBooking(m)}>Stornieren</Button>
              </span>
            </div>
          ))}
          {lookupOpen && (
            <div className="manage-lookup">
              <Input placeholder="Dein Nachname" value={lookupName} style={{ flex: 1 }}
                onInput={(e) => setLookupName(e.target.value)} />
              <Button design="Emphasized" disabled={lookupBusy} onClick={findByName}>{lookupBusy ? "Suche…" : "Buchung finden"}</Button>
            </div>
          )}
        </div>
      )}
      {!lookupOpen && (
        <Button design="Transparent" icon="search" style={{ marginTop: 6 }} onClick={() => setLookupOpen(true)}>
          Schon gebucht? Termin verwalten
        </Button>
      )}

      {rescheduleToken && (
        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <MessageStrip design="Warning" hideCloseButton style={{ flex: 1, minWidth: 220 }}>
            Umbuchung – bitte wähle unten einen neuen Termin.
          </MessageStrip>
          <Button design="Transparent" onClick={() => { setRescheduleToken(null); setSelectedSlot(null); }}>Abbrechen</Button>
        </div>
      )}

      {allowedDurations && (
        <div className="duration-picker" role="group" aria-label="Dauer">
          {allowedDurations.map((d) => (
            <Button key={d} design={selectedDuration === d ? "Emphasized" : "Default"} onClick={() => chooseDuration(d)}>
              {durationLabel(d)}
            </Button>
          ))}
        </div>
      )}

      {!hasAnySlots ? (
        <MessageStrip design="Information" hideCloseButton style={{ marginTop: 16 }}>
          Aktuell sind keine Termine verfügbar. Bitte schau später wieder vorbei.
        </MessageStrip>
      ) : !hasBookable ? (
        <MessageStrip design="Information" hideCloseButton style={{ marginTop: 16 }}>
          Für die gewählte Dauer ({durationLabel(selectedDuration)}) sind aktuell keine zusammenhängenden Zeiten frei. Bitte wähle eine kürzere Dauer.
        </MessageStrip>
      ) : (
        <>
          <div className="cal-header" ref={calRef}>
            <Button design="Transparent" icon="slim-arrow-left" disabled={!canPrev} onClick={() => step(-1)} tooltip="Voriger Monat" />
            <span className="cal-month">{monthLabel(cursor.year, cursor.month)}</span>
            <Button design="Transparent" icon="slim-arrow-right" disabled={!canNext} onClick={() => step(1)} tooltip="Nächster Monat" />
          </div>

          <div className="cal-grid">
            {WEEKDAYS.map((w) => <div key={w} className="cal-weekday">{w}</div>)}
            {cells.map((d) => {
              const inMonth = d.getMonth() === cursor.month;
              const iso = isoFromDate(d);
              // Any visible day with free slots is bookable — including spill-over cells from the
              // adjacent month (e.g. the 1st/2nd shown in the previous month's last row).
              const has = (bookableByDate.get(iso) || 0) > 0;
              const cls = ["cal-day"];
              if (has) cls.push("has-slots");
              else if (!inMonth) cls.push("outside");
              else cls.push("empty");
              if (selectedDate === iso) cls.push("selected");
              return (
                <div key={iso} className={cls.join(" ")} onClick={has ? () => selectDay(iso) : undefined}>
                  <span>{d.getDate()}</span>
                  <span className={"dot" + (has ? "" : " placeholder")} />
                </div>
              );
            })}
          </div>

          <div className="cal-legend"><span className="dot" /> Tage mit freien Terminen</div>

          {selectedDate && (
            <div className="cal-times" ref={timesRef}>
              <h3>Freie Zeiten am {longDate(selectedDate)}</h3>
              {dayStarts.length === 0 ? (
                <Text className="muted">
                  Für die gewählte Dauer ({durationLabel(selectedDuration)}) sind an diesem Tag keine zusammenhängenden Zeiten frei.
                </Text>
              ) : (
                <div className="slot-grid">
                  {dayStarts.map((slot) => (
                    <Button key={slot.id} design={selectedSlot?.id === slot.id ? "Emphasized" : "Default"} onClick={() => selectTime(slot)}>
                      {slot.time}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}

          {selectedSlot && (
            <div ref={formRef} style={{ marginTop: 24, borderTop: "1px solid #e1e6ec", paddingTop: 20 }}>
              <Title level="H3" style={{ marginBottom: 12 }}>
                {rescheduleToken ? "Umbuchen auf " : "Termin am "}{longDate(selectedDate)}, {selectedSlot.time}–{endTimeLabel(selectedSlot, selectedDuration)} Uhr
                {selectedDuration ? ` (${durationLabel(selectedDuration)})` : ""}
              </Title>
              {rescheduleToken ? (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <Button design="Emphasized" disabled={submitting} onClick={doReschedule}>
                    {submitting ? "Wird umgebucht…" : "Auf diesen Termin umbuchen"}
                  </Button>
                  <Button design="Transparent" disabled={submitting} onClick={() => { setRescheduleToken(null); setSelectedSlot(null); }}>
                    Abbrechen
                  </Button>
                </div>
              ) : (
                <div style={{ display: "grid", gap: 14, maxWidth: 420 }}>
                  <div>
                    <Label required>Name</Label>
                    <Input id="bk-name" value={form.customerName}
                      valueState={fieldErrors.customerName ? "Negative" : "None"}
                      valueStateMessage={<span>{fieldErrors.customerName}</span>}
                      style={{ width: "100%" }} onInput={(e) => setForm({ ...form, customerName: e.target.value })} />
                  </div>
                  <div>
                    <Label required>E-Mail</Label>
                    <Input id="bk-email" type="Email" value={form.customerEmail}
                      valueState={fieldErrors.customerEmail ? "Negative" : "None"}
                      valueStateMessage={<span>{fieldErrors.customerEmail}</span>}
                      style={{ width: "100%" }} onInput={(e) => setForm({ ...form, customerEmail: e.target.value })} />
                  </div>
                  <CheckBox id="bk-privacy" text="Ich stimme der Verarbeitung meiner Daten zur Terminabwicklung zu."
                    checked={form.privacyAccepted}
                    valueState={fieldErrors.privacyAccepted ? "Negative" : "None"}
                    onChange={(e) => setForm({ ...form, privacyAccepted: e.target.checked })} />
                  {privacyNotice && <Text className="muted" style={{ fontSize: 12 }}>{privacyNotice}</Text>}
                  <div>
                    <Button id="bk-submit" design="Emphasized" disabled={submitting} onClick={submit}>
                      {submitting ? "Wird gebucht…" : "Verbindlich buchen"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Shell>
  );
}
